<template>
    <div class="w-full">
        <!-- Processing state -->
        <div v-if="isProcessing && !hasError" class="rounded-lg border border-blue-200 bg-blue-50/50 overflow-hidden shadow-sm">
            <div class="px-4 py-3 flex items-center gap-2 text-sm text-blue-600">
                <UIcon v-if="active" name="i-heroicons-arrow-path" class="w-4 h-4 animate-spin" />
                <span>{{ active ? 'Preparing campaign plan...' : 'Campaign plan incomplete. No plan was finalized.' }}</span>
            </div>
        </div>

        <!-- Error state -->
        <div v-else-if="hasError" class="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
            <div class="flex items-center gap-2 text-sm text-red-700">
                <UIcon name="i-heroicons-x-circle" class="w-4 h-4" />
                <span>{{ part.errorText || 'Failed to generate plan' }}</span>
            </div>
        </div>

        <!-- Plan card -->
        <div v-else-if="plan" class="rounded-lg border border-slate-200 bg-white overflow-hidden shadow-sm">
            <div v-if="isDraft" class="px-4 py-2 border-b border-blue-100 bg-blue-50 text-xs text-blue-700" role="status">
                {{ active ? 'Drafting strategy… Not ready for review.' : 'Draft incomplete. Not ready for review.' }}
            </div>
            <!-- Header -->
            <div class="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                <div class="flex items-center gap-2">
                    <UIcon name="i-heroicons-rocket-launch" class="w-4.5 h-4.5 text-blue-600 flex-shrink-0" />
                    <h3 class="font-semibold text-sm text-slate-900">{{ plan.campaignName || 'Campaign proposal' }}</h3>
                    <span v-if="!isDraft && plan.status === 'proposal'" class="text-xs text-slate-500">Proposed strategy</span>
                </div>
                <p class="text-xs text-slate-500 mt-0.5 ml-6.5">{{ plan.goal }}</p>
            </div>

            <!-- Sections -->
            <div class="divide-y divide-slate-100">
                <CampaignEligibilityPolicySettings
                    v-if="!isDraft && plan.status !== 'proposal' && savedCampaignId"
                    :key="savedCampaignId"
                    :campaign-id="savedCampaignId"
                    surface="plan"
                />
                <!-- Targeting -->
                <div v-if="!isDraft || plan.targeting.companies || plan.targeting.contacts || plan.targeting.language" class="px-4 py-3">
                    <div class="flex items-start gap-2">
                        <UIcon name="i-heroicons-user-group" class="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div class="flex-1 min-w-0 space-y-1">
                            <h4 class="font-semibold text-xs text-slate-900 uppercase tracking-wide">Targeting</h4>
                            <div class="text-sm space-y-0.5">
                                <div v-if="!isDraft || plan.targeting.companies" class="flex items-start gap-2">
                                    <span class="text-slate-400 text-[10px] uppercase tracking-wider font-medium w-20 flex-shrink-0 pt-0.5">Companies</span>
                                    <span class="text-slate-700">{{ plan.targeting.companies }}</span>
                                </div>
                                <div v-if="!isDraft || plan.targeting.contacts" class="flex items-start gap-2">
                                    <span class="text-slate-400 text-[10px] uppercase tracking-wider font-medium w-20 flex-shrink-0 pt-0.5">Contacts</span>
                                    <span class="text-slate-700">{{ plan.targeting.contacts }}</span>
                                </div>
                                <div v-if="plan.targeting.language" class="flex items-start gap-2">
                                    <span class="text-slate-400 text-[10px] uppercase tracking-wider font-medium w-20 flex-shrink-0 pt-0.5">Language</span>
                                    <span class="text-slate-700">{{ plan.targeting.language }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- AI Lead Research -->
                <div v-if="!isDraft || plan.personalization" class="px-4 py-3">
                    <div class="flex items-start gap-2">
                        <UIcon name="i-heroicons-light-bulb" class="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                        <div class="flex-1 min-w-0 space-y-1">
                            <h4 class="font-semibold text-xs text-slate-900 uppercase tracking-wide">AI Lead Research</h4>
                            <p class="text-sm text-slate-700">{{ plan.personalization || 'No additional personalization research. Qualification filters are listed in Targeting.' }}</p>
                        </div>
                    </div>
                </div>

                <!-- Messaging Angles -->
                <div v-if="!isDraft || plan.angle || plan.exampleOutreach.subject || plan.exampleOutreach.body || plan.exampleOutreach.sequenceOverview" class="px-4 py-3">
                    <div class="flex items-start gap-2">
                        <UIcon name="i-heroicons-envelope" class="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <div class="flex-1 min-w-0 space-y-1">
                            <h4 class="font-semibold text-xs text-slate-900 uppercase tracking-wide">Messaging Angles</h4>
                            <p class="text-sm text-slate-700">{{ plan.angle }}</p>
                            <p class="text-xs text-slate-500">{{ plan.exampleOutreach.kind === 'real-contact-preview' ? `Example message for ${plan.exampleOutreach.contactName || 'this contact'}. Exact saved preview; claims still need review.` : 'Example message. Illustrative, not researched for a real contact.' }}</p>
                            <div class="text-sm space-y-2">
                                <p class="text-xs text-slate-500 leading-relaxed">{{ plan.exampleOutreach.sequenceOverview }}</p>
                                <div v-if="!isDraft || plan.exampleOutreach.subject || plan.exampleOutreach.body" class="rounded-md border border-slate-200 bg-slate-50/80 overflow-hidden">
                                    <div v-if="plan.exampleOutreach.subject" class="px-3 py-1.5 border-b border-slate-200 bg-white">
                                        <span class="text-[10px] text-slate-400 uppercase tracking-wider font-medium mr-1.5">Subject:</span>
                                        <span class="text-slate-800 text-sm font-medium">{{ plan.exampleOutreach.subject }}</span>
                                    </div>
                                    <div class="px-3 py-2 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{{ plan.exampleOutreach.body }}</div>
                                </div>
                            </div>
                            <details v-if="plan.exampleOutreach.evidence?.length" class="text-xs text-slate-500">
                                <summary>Research excerpts used as context</summary>
                                <p v-for="source in plan.exampleOutreach.evidence" :key="source.resultId" class="mt-2 whitespace-pre-wrap">Research #{{ source.resultId }}: {{ source.excerpt }}</p>
                            </details>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
interface CampaignPlan {
    status?: 'proposal';
    campaignName: string;
    goal: string;
    targeting: { companies: string; contacts: string; language?: string };
    angle: string;
    personalization: string;
    exampleOutreach: { kind?: string; receiptId?: string; contactName?: string | null; subject?: string; body: string; sequenceOverview: string; evidence?: { resultId: number; excerpt: string }[] };
}

const props = defineProps<{
    // Passed separately because the SDK replaces input while mutating the same tool part.
    // A child computed that only reads part.input can otherwise retain the previous delta.
    input?: unknown;
    active: boolean;
    part: {
        type: string;
        toolCallId?: string;
        state?: string;
        input?: unknown;
        output?: unknown;
        errorText?: string;
        [key: string]: unknown;
    };
}>();

const savedCampaignId = computed(() => {
    const id = record(props.input ?? props.part.input)?.campaignId;
    return typeof id === 'number' && Number.isInteger(id) && id > 0 ? id : null;
});

const hasError = computed(() =>
    props.part.state === 'output-error' ||
    (props.part.errorText !== undefined && props.part.errorText !== '')
);

function record(value: unknown): Record<string, unknown> | undefined {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : undefined;
}

function text(value: unknown): string {
    return typeof value === 'string' ? value : '';
}

const draft = computed<CampaignPlan | null>(() => {
    if (hasError.value || props.part.output !== undefined) return null;
    const input = record(props.input);
    // Saved plans and real-contact previews must come from authoritative server output.
    if (!input || input.campaignId !== undefined) return null;
    const proposal = record(input.proposal);
    const example = record(input.exampleOutreach);
    if (!proposal || (example?.kind !== undefined && example.kind !== 'hypothetical')) return null;
    const targeting = record(proposal.targeting);
    return {
        status: 'proposal',
        campaignName: text(proposal.campaignName),
        goal: text(input.goal),
        targeting: {
            companies: text(targeting?.companies),
            contacts: text(targeting?.contacts),
            language: text(targeting?.language),
        },
        angle: text(input.angle),
        personalization: text(proposal.personalization),
        exampleOutreach: {
            kind: 'hypothetical',
            subject: text(example?.subject),
            body: text(example?.body),
            sequenceOverview: text(example?.sequenceOverview),
        },
    };
});

const isDraft = computed(() => draft.value !== null);
const isProcessing = computed(() => props.part.output === undefined && !draft.value);

const plan = computed<CampaignPlan | null>(() => {
    if (hasError.value) return null;
    if (props.part.output !== undefined) {
        const data = props.part.output as CampaignPlan;
        return data?.campaignName ? data : null;
    }
    return draft.value;
});
</script>
